import type { AcpUsageData } from '@crowdex/agent-contracts';
import { Button } from '@crowdex/ui/components/button';
import { cn } from '@crowdex/ui/lib/utils';
import { Cloud, FileBox, FolderGit2, GitBranch, PanelRight, X } from 'lucide-react';
import { type ReactNode, useEffect, useRef } from 'react';
import {
  type DesktopConnectionState,
  DesktopConnectionStatus,
} from '@/components/desktop-connection-status';
import { ConversationBranchChip } from '@/components/desktop-shell/branch-actions-menu';
import {
  ChildConversationBreadcrumb,
  WorkflowTaskChip,
} from '@/components/desktop-shell/child-conversation';
import { UsageRing } from '@/components/desktop-shell/composer-usage-indicator';
import {
  ConversationMenu,
  type ConversationMenuActions,
  useConversationMenu,
  useConversationMenuShortcuts,
  useProjectMenu,
} from '@/components/desktop-shell/conversation-menu';
import { ThreadSummaryToggle } from '@/components/desktop-shell/thread-summary';
import { repositoryLabel } from '@/components/desktop-summary/summary-workspace-section';
import { useArtifactIndex } from '@/features/apps/use-artifact-index';
import { AssistantHeading } from '@/features/assistants/assistant-heading';
import { useAssistantOfConversation } from '@/features/assistants/use-assistant';
import { PullRequestHeaderChip } from '@/features/pull-request/pull-request-header-chip';
import {
  selectSharedFolderPullRequest,
  selectWorktreeSourceRoot,
} from '@/lib/desktop-shell/conversation-worktree';
import { primaryCheckoutPath } from '@/lib/desktop-shell/execution-mode-controls';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { useWorkspaceName } from '@/lib/desktop-shell/use-workspace-name';
import { matchesShortcut, shortcutLabel } from '@/lib/platform';
import {
  currentShortcut,
  TOGGLE_PANEL_SHORTCUT,
  useShortcutOverrides,
} from '@/lib/settings-page/keyboard-shortcuts';

export type { ConversationMenuActions } from '@/components/desktop-shell/conversation-menu';

const CONVERSATION_ID = /^[0-9a-f-]{36}$/i;

/** ⌘J toggles the side panel, wherever the focus is (the header need not be shown). */
export function usePanelShortcut() {
  const setSidePanelOpen = useDesktopShellStore((state) => state.setSidePanelOpen);
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const keys = currentShortcut('toggle-panel') ?? TOGGLE_PANEL_SHORTCUT;
      if (event.defaultPrevented || !matchesShortcut(event, keys)) return;
      event.preventDefault();
      setSidePanelOpen((open) => !open);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [setSidePanelOpen]);
}

export type ConversationHeaderProps = {
  conversationTitle: string;
  preview?: boolean;
  executionMode?: 'local' | 'worktree' | 'cloud';
  /** The conversation's context usage, shown as a ring that opens the model popover. */
  usage?: AcpUsageData | null;
  /** What only the shell does for the ⋮ menu (the repository's setup, the handoff). */
  actions?: ConversationMenuActions;
  /**
   * The other half of a split (D56): a conversation that is not the one the window runs, named
   * by its own title and folder, with a button that shows its panel and one that closes it.
   */
  secondary?:
    | {
        conversationId: string;
        workspaceRoot: string | null;
        onActivate: () => void;
        onClose: () => void;
      }
    | undefined;
  /** The window's connection to its server, shown when it is not plain ready. */
  connectionState?: DesktopConnectionState;
};

/** A chip of what the conversation is attached to: its repository, its branch, an artifact. */
function AttachmentChip({
  children,
  icon,
  mono = false,
  title,
}: {
  children: ReactNode;
  icon: ReactNode;
  mono?: boolean;
  title?: string;
}) {
  return (
    <span
      className={cn(
        // The title gives way first, down to its minimum (N6): a share small enough that no
        // fraction of a pixel cuts the chip's text meanwhile. Then it does, down to its icon,
        // and a narrow header has none.
        'type-meta flex min-w-3.5 max-w-48 shrink-[0.001] items-center gap-1.5 overflow-hidden text-muted-foreground @max-[28rem]:hidden',
        mono && 'font-mono',
      )}
      title={title}
    >
      <span className="shrink-0 [&_svg]:size-3.5">{icon}</span>
      <span className="truncate">{children}</span>
    </span>
  );
}

/**
 * The conversation's header (D5): its title, what it is attached to (repository and branch, or
 * an artifact when it has no repository, D5c), its context usage, the panel and Summary buttons
 * and its ⋮ menu (D6), whose shortcuts it answers. The single home of these controls, for one
 * conversation and for each half of a split (`secondary`: the other half).
 */
export function ConversationHeader({
  conversationTitle,
  preview = false,
  executionMode: activeExecutionMode,
  usage = null,
  actions = {},
  connectionState,
  secondary,
}: ConversationHeaderProps) {
  const chatId = useDesktopShellStore((state) => state.chatId);
  const taskId = secondary?.conversationId ?? chatId;
  const savedExecutionMode = useDesktopShellStore((state) => state.executionMode);
  const executionMode = activeExecutionMode ?? savedExecutionMode;
  const storeGitStatus = useDesktopShellStore((state) => state.gitStatus);
  const gitStatus = secondary === undefined ? storeGitStatus : null;
  const storeWorkspace = useDesktopShellStore((state) => state.workspace);
  const worktreeSourceRoot = useDesktopShellStore(selectWorktreeSourceRoot);
  const sharedFolderPullRequest = useDesktopShellStore(selectSharedFolderPullRequest);
  const workspace = secondary === undefined ? storeWorkspace : secondary.workspaceRoot;
  const personalWorkspaceRoot = useDesktopShellStore((state) => state.personalWorkspaceRoot);
  const sidePanelOpen = useDesktopShellStore((state) => state.sidePanelOpen);
  const setSidePanelOpen = useDesktopShellStore((state) => state.setSidePanelOpen);
  const setModelMenuOpen = useDesktopShellStore((state) => state.setModelMenuOpen);
  const modelMenuOpen = useDesktopShellStore((state) => state.modelMenuOpen);
  // The usage ring opens the model's popover: once it closes, the focus comes back to the ring.
  const ringRef = useRef<HTMLSpanElement>(null);
  const openedByRing = useRef(false);
  useEffect(() => {
    if (modelMenuOpen || !openedByRing.current) return;
    openedByRing.current = false;
    // After the popover's own focus return (to the model chip it is anchored to).
    const timer = window.setTimeout(() => ringRef.current?.querySelector('button')?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [modelMenuOpen]);
  const pullRequestStatus = useDesktopShellStore(
    (state) => state.appSettings.pullRequestStatusInHeader,
  );
  // The pinned summary shows the pull request (D5): the header has no chip of it then.
  const summaryShown = useDesktopShellStore(
    (state) => state.summaryPinned && state.summaryMode !== null && state.summaryMode !== 'overlay',
  );
  const overrides = useShortcutOverrides();
  // A project's own conversation is the project: its name and its members (D65, lot 6).
  const project = useAssistantOfConversation(taskId);
  // A worktree is its repository's: named as the server names it, not by its own folder.
  const repositoryRoot =
    secondary === undefined
      ? (worktreeSourceRoot ?? primaryCheckoutPath(gitStatus) ?? workspace)
      : workspace;
  const workspaceName = useWorkspaceName(workspace);
  // The personal workspace is no repository: the conversation shows what it carries instead.
  const repository =
    repositoryRoot === null || repositoryRoot === personalWorkspaceRoot
      ? null
      : repositoryLabel(repositoryRoot, workspaceName);
  const branch = repository === null ? null : (gitStatus?.branch ?? null);
  // Without a repository, the conversation is named by what it made (a design, a video).
  const { artifacts } = useArtifactIndex(CONVERSATION_ID.test(taskId) ? taskId : null, {
    enabled: repository === null,
  });
  const attached = repository === null ? artifacts.slice(0, 1) : [];
  // The branch's actions (lot 5b's chip) belong to the conversation the window runs.
  const branchActions = branch !== null && secondary === undefined;
  const menu = useConversationMenu({
    // The other half's menu acts on its conversation only; the branch is its chip's (D45).
    actions:
      secondary === undefined
        ? {
            ...actions,
            onRepositorySetup: repository === null ? undefined : actions.onRepositorySetup,
          }
        : {},
    conversationId: taskId,
    surface: 'desktop',
  });
  const projectMenu = useProjectMenu(project, taskId);
  // The other half's side chat and find are this one's; a project's own ⋮ is the project's.
  const entries =
    project !== null
      ? projectMenu.entries
      : secondary === undefined
        ? menu.entries
        : menu.entries.filter((entry) => entry.id !== 'side-chat' && entry.id !== 'find');
  useConversationMenuShortcuts(menu.run, entries, secondary === undefined);

  return (
    <header
      // With the sidebar hidden or a rail it starts after the window's traffic lights; on Windows
      // and Linux it ends before the window controls. It is the window's drag region.
      className="desktop-header @container relative flex h-14 shrink-0 items-center gap-3 pr-[max(0.75rem,var(--window-controls-inset,0px))] pl-[max(1.5rem,var(--shell-leading-inset,0px))]"
      data-electron-drag-region
      data-testid="conversation-header"
    >
      <div
        className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden"
        data-electron-drag-region
      >
        {/* Lot 5b: a child conversation's way back to its parent (D49): the parent's name
            stays readable, the child's title gives way first. */}
        <span className="flex min-w-0 max-w-[40%] shrink-0 empty:hidden">
          <ChildConversationBreadcrumb conversationId={taskId} />
        </span>
        <h1
          // A project's name stays whole while its heading gives way (D65).
          // A conversation's title gives way before its chips, down to a readable width (N6).
          className={`type-card-title pointer-events-none truncate text-foreground ${project === null ? 'min-w-[min(10rem,30cqw)] shrink-[1000]' : 'min-w-12 max-w-[60%] shrink'}`}
          title={project?.name ?? conversationTitle}
        >
          {project?.name ?? conversationTitle}
        </h1>
        {project !== null && <AssistantHeading conversationId={taskId} project={project} />}
        {/* A workflow's task says which, right after its name (D49). */}
        <WorkflowTaskChip conversationId={taskId} />
        {repository !== null && project === null && (
          <AttachmentChip
            icon={executionMode === 'cloud' && secondary === undefined ? <Cloud /> : <FolderGit2 />}
            title={repositoryRoot ?? undefined}
          >
            {repository}
          </AttachmentChip>
        )}
        {/* Lot 5b: the branch's actions (D45) and the workflow task chip (D49). */}
        {branchActions && project === null && (
          <span
            className="flex min-w-12 max-w-48 shrink-[0.001] overflow-hidden"
            data-electron-no-drag-region
          >
            <ConversationBranchChip />
          </span>
        )}
        {branch !== null && secondary !== undefined && (
          <AttachmentChip icon={<GitBranch />} mono title={branch}>
            {branch}
          </AttachmentChip>
        )}
        {attached.map((artifact) => (
          <AttachmentChip
            icon={<FileBox />}
            key={artifact.id}
            title={`${artifact.typeTitle} · ${artifact.title}`}
          >
            {artifact.title}
          </AttachmentChip>
        ))}
      </div>
      <div className="flex shrink-0 items-center gap-1" data-electron-no-drag-region>
        {connectionState !== undefined && <DesktopConnectionStatus state={connectionState} />}
        {/* The pull request shows in the summary too: a narrow header leaves it there, and so
            does a header beside the pinned summary. */}
        {secondary === undefined && project === null && !summaryShown && (
          <span className="hidden @[52rem]:contents">
            <PullRequestHeaderChip
              enabled={pullRequestStatus}
              session={{
                taskId,
                workspace,
                cloud: executionMode === 'cloud',
                visible: true,
                preview,
                gitStatus,
                claim: preview || executionMode === 'cloud' ? undefined : sharedFolderPullRequest,
              }}
            />
          </span>
        )}
        {usage !== null && secondary === undefined && (
          <span className="contents" ref={ringRef}>
            <UsageRing
              onOpen={() => {
                openedByRing.current = true;
                setModelMenuOpen(true);
              }}
              usage={usage}
            />
          </span>
        )}
        {secondary === undefined ? (
          <Button
            aria-label={sidePanelOpen ? 'Close the panel' : 'Open the panel'}
            aria-pressed={sidePanelOpen}
            onClick={() => setSidePanelOpen((open) => !open)}
            size="icon-sm"
            title={`Panel (${shortcutLabel(currentShortcut('toggle-panel', overrides) ?? TOGGLE_PANEL_SHORTCUT)})`}
            variant={sidePanelOpen ? 'secondary' : 'ghost'}
          >
            <PanelRight className="size-4" />
          </Button>
        ) : (
          <Button
            aria-label={`Show the side panel of ${conversationTitle}`}
            onClick={(event) => {
              event.stopPropagation();
              secondary.onActivate();
            }}
            onPointerDown={(event) => event.stopPropagation()}
            size="icon-sm"
            title="Show its panel"
            variant="ghost"
          >
            <PanelRight className="size-4" />
          </Button>
        )}
        {secondary === undefined && <ThreadSummaryToggle />}
        <ConversationMenu entries={entries} title={project?.name ?? conversationTitle} />
        {project === null ? menu.dialogs : projectMenu.dialogs}
        {secondary !== undefined && (
          <Button
            aria-label={`Close ${conversationTitle}`}
            onClick={(event) => {
              event.stopPropagation();
              secondary.onClose();
            }}
            onPointerDown={(event) => event.stopPropagation()}
            size="icon-sm"
            title="Close"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        )}
      </div>
    </header>
  );
}
